import React from "react";

const statusLabel = (status) =>
  status === "complete" ? "Completed" : status === "started" ? "Started" : "Scheduled";

// Shared compact day cell used by the Daily Devotion header's calendar
// popover and the My Devotions calendar. Full-mode (non-compact) is kept
// for callers that still want the inline passage/status rows.
const DevotionCalendarCell = ({ day, selected, today, compact = false, onSelect }) => {
  if (!day)
    return <div className="min-h-[44px]" aria-hidden="true" />;

  const passage = day.passages?.[0];
  const dayNumber = Number(day.date.slice(-2));

  const spokenBits = [
    selected ? "selected" : null,
    day.status === "complete" ? "reflection saved" : null,
    day.status === "started" ? "reflection started" : null,
    day.has_sunday_preparation ? "Sunday preparation" : null,
    today ? "today" : null,
  ].filter(Boolean);
  const label = `${day.date}${passage?.reference ? `, ${passage.reference}` : ""}${
    spokenBits.length ? ", " + spokenBits.join(", ") : ""
  }`;

  // ---- Compact variant: shown inside the popover + My Devotions calendar.
  if (compact) {
    const base =
      "min-h-[44px] w-full rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cta focus-visible:ring-offset-1";
    const stateClass = selected
      ? "bg-accent-cta text-white shadow-sm"
      : today
        ? "text-accent-cta border border-accent-cta bg-white/60"
        : "text-neu-primary bg-white/50 border border-white/60 hover:bg-white/80";
    const dotColor = (color) => (
      <span
        aria-hidden="true"
        className={`w-1.5 h-1.5 rounded-full ${selected ? "bg-white" : color}`}
      />
    );
    return (
      <button
        type="button"
        onClick={() => onSelect(day.date)}
        aria-label={label}
        aria-pressed={selected}
        aria-current={today ? "date" : undefined}
        className={`${base} ${stateClass}`}
      >
        <span className="text-label-md font-semibold leading-none">{dayNumber}</span>
        <span className="flex items-center gap-0.5 h-1.5">
          {(day.status === "complete" || day.status === "started") &&
            dotColor("bg-[#3b82f6]")}
          {day.has_sunday_preparation && dotColor("bg-accent-cta")}
        </span>
      </button>
    );
  }

  // ---- Full variant (unchanged behavior, modernized visuals).
  const fullState = selected
    ? "border-accent-cta bg-accent-cta/10"
    : today
      ? "border-accent-cta bg-white"
      : "border-gray-200 bg-white hover:bg-gray-50";
  return (
    <button
      type="button"
      onClick={() => onSelect(day.date)}
      aria-label={label}
      aria-pressed={selected}
      aria-current={today ? "date" : undefined}
      className={`min-h-[96px] w-full border rounded-lg p-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cta ${fullState}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-900">{dayNumber}</span>
        <span
          aria-hidden="true"
          className={`w-2 h-2 rounded-full ${
            day.status === "complete"
              ? "bg-accent-cta"
              : day.status === "started"
                ? "bg-[#3b82f6]"
                : "bg-transparent border border-gray-300"
          }`}
        />
      </div>
      {passage?.reference && (
        <p className="mt-2 line-clamp-2 text-xs text-gray-700">{passage.reference}</p>
      )}
      <p className="mt-1 text-xs text-gray-600">
        {statusLabel(day.status)}
        {day.has_sunday_preparation ? " · Sunday" : ""}
      </p>
    </button>
  );
};

export { statusLabel };
export default DevotionCalendarCell;
